import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export interface ButtonProps {
  label: string | React.ReactNode;
  styleType?: 'primary' | 'secondary' | 'tertiary' | 'rounded' | 'icon';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  onClick: () => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  className?: string;
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  autoFocus?: boolean;
  'data-testid'?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      label,
      styleType = 'primary',
      size = 'md',
      disabled = false,
      onClick,
      startIcon,
      endIcon,
      className,
      id,
      type = 'button',
      form,
      autoFocus = false,
      ...rest
    },
    ref
  ) => {
    const getButtonClasses = () => {
      const disabledClasses = `disabled:bg-chariot-stroke disabled:cursor-not-allowed disabled:text-chariot-text-secondary`;
      const baseClasses = `w-fit font-bold rounded transition-colors duration-200 flex items-center justify-center space-x-2`;

      const sizeClasses = {
        xs: 'py-1 px-2 text-sm',
        sm: 'py-1.5 px-3 text-sm',
        md: 'py-2 px-4 text-base',
        lg: 'py-2.5 px-5 text-base',
        xl: 'py-3 px-6 text-base',
      };

      switch (styleType) {
        case 'primary':
          return twMerge(
            baseClasses,
            sizeClasses[size],
            disabledClasses,
            'bg-chariot-brand-primary text-white hover:bg-chariot-brand-secondary'
          );
        case 'secondary':
          return twMerge(
            baseClasses,
            sizeClasses[size],
            disabledClasses,
            'border border-chariot-brand-primary text-chariot-text-primary hover:bg-chariot-background-secondary disabled:border-none'
          );
        case 'tertiary':
          return twMerge(
            baseClasses,
            sizeClasses[size],
            disabledClasses,
            'text-chariot-text-primary border border-chariot-stroke hover:text-chariot-text-primary hover:border-chariot-text-primary disabled:border-none'
          );
        case 'rounded':
          return twMerge(
            baseClasses,
            sizeClasses[size],
            disabledClasses,
            'font-medium border border-chariot-brand-primary text-chariot-brand-primary hover:bg-chariot-brand-primary hover:text-white rounded-full disabled:border-0'
          );
        case 'icon':
          return twMerge(
            'transition-colors duration-200 items-center rounded-full hover:bg-chariot-brand-primary/10 text-chariot-text-secondary',
            sizeClasses[size]
          );
        default:
          return twMerge(baseClasses, sizeClasses[size], disabledClasses);
      }
    };

    return (
      <button
        ref={ref}
        className={twMerge(getButtonClasses(), className)}
        disabled={disabled}
        onClick={onClick}
        id={id}
        type={type}
        form={form}
        autoFocus={autoFocus}
        {...rest}
      >
        {startIcon && <span>{startIcon}</span>}
        <span className="whitespace-nowrap">{label}</span>
        {endIcon && <span>{endIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
